import { useEffect, useState, useCallback } from "react";

import confetti from "canvas-confetti";
import { Loader2, Clock, Mail, CheckCircle, Users, Shield, Info, PartyPopper } from "lucide-react";
import { useNavigate } from "react-router-dom";

import howlyfaceDark from "@/assets/howlyface-dark.png";
import howlyfaceLight from "@/assets/howlyface-light.png";
import { useTheme } from "@/components/ThemeProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useRealtimeChannel } from "@/hooks/useRealtimeChannel";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { hashIdentifier } from "@/lib/authHelpers";

const Waitlist = () => {
  const { user, signOut, session } = useAuth();
  const { role, loading } = useUserRole();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { execute: sendVerificationEmail, isLoading: isSending } = useEdgeFunction({ auth: true });
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [emailVerified, setEmailVerified] = useState(true);
  const [communicationCode, setCommunicationCode] = useState<string | null>(null);
  const [isApproved, setIsApproved] = useState(false);
  const [channelName, setChannelName] = useState<string | null>(null);
  const { status: realtimeStatus } = useRealtime();

  // Determine which mascot to show based on theme
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const mascotImage = isDark ? howlyfaceDark : howlyfaceLight;

  useEffect(() => {
    if (!loading && (role === "user" || role === "admin")) {
      navigate("/dashboard");
    }
  }, [role, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      // Fetch verification status and communication code
      const { data: profile } = await supabase
        .from("profiles")
        .select("email_verified, communication_code")
        .eq("id", user.id)
        .single();
      
      setEmailVerified(profile?.email_verified ?? true);
      setCommunicationCode(profile?.communication_code || null);

      // Fetch queue position
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("queue_position")
        .eq("user_id", user.id)
        .eq("role", "waitlisted")
        .single();

      if (roleData?.queue_position) {
        setQueuePosition(roleData.queue_position);
      }
    };

    if (user) {
      fetchData();
      hashIdentifier(user.id).then(hash => {
        setChannelName(`private-notifications:${hash}`);
      });
    }
  }, [user]);

  const handleApproval = useCallback(() => {
    setIsApproved(true);
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
    });
    toast.success("🎉 Your account has been approved! Welcome to AdvisorMuse!", {
      duration: 5000,
    });
  }, []);

  useRealtimeChannel({
    channelName: realtimeStatus === 'connected' && channelName ? channelName : '',
    type: 'broadcast',
    event: 'account_approved',
    onMessage: handleApproval,
  });

  const handleResendVerification = async () => {
    if (!session) {
      toast.error("Could not get user session. Please sign out and sign in again.");
      return;
    }
    
    const confirmationUrl = `${window.location.origin}/verify-email?type=signup&token_hash=${session.access_token}`;
    
    const { error } = await sendVerificationEmail("send-verification-email", {
      body: {
        email: user!.email,
        confirmationUrl: confirmationUrl,
      },
      showErrorToast: true,
    });

    if (!error) {
      toast.success("Verification email sent! Please check your inbox.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
              <PartyPopper className="h-10 w-10 text-green-500" />
            </div>
            <CardTitle className="text-3xl font-bold">You're In!</CardTitle>
            <CardDescription className="text-base">
              Your account has been approved. Welcome to AdvisorMuse!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl space-y-8">
        {/* Mascot */}
        <div className="flex justify-center">
          <img 
            src={mascotImage} 
            alt="Howlyface Mascot" 
            className="w-40 h-40 object-contain animate-fade-in"
          />
        </div>

        <Card className="w-full">
          {!emailVerified && (
            <div className="m-6 mb-0 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="text-yellow-600 dark:text-yellow-500 text-xl">⚠️</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                    Email Not Verified
                  </h3>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                    Verify your email to move up in the waitlist queue! Verified users get priority access.
                  </p>
                  <Button
                    onClick={handleResendVerification}
                    variant="outline"
                    size="sm"
                    className="border-yellow-600 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-500 dark:text-yellow-400 dark:hover:bg-yellow-900/40"
                    disabled={isSending}
                  >
                    {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Resend Verification Email
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <CardHeader className="text-center space-y-6 pb-4">
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>
            <div className="space-y-3">
              <CardTitle className="text-3xl font-bold">Thank You for Your Interest! 🎉</CardTitle>
              <CardDescription className="text-base leading-relaxed">
                We're excited to have you join our journey. Your registration has been received.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-8">
            {/* Communication Code Section */}
            {communicationCode && (
              <div className="p-6 bg-linear-to-br from-primary/10 via-primary/5 to-background rounded-lg border-2 border-primary/20">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground text-lg mb-1">Your Security Code</h3>
                    <p className="text-sm text-muted-foreground">
                      This code will appear in all emails from us to protect you from phishing
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-3 p-4 bg-background rounded-lg border border-border mb-4">
                  <Badge variant="outline" className="text-2xl font-bold px-6 py-2 bg-primary/5">
                    {communicationCode}
                  </Badge>
                </div>
                
                <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-md">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    <strong>Anti-Phishing Protection:</strong> Every legitimate email from AdvisorMuse will display this code. 
                    If you receive an email without this code, it's not from us. Never click links in emails that don't show your code.
                  </p>
                </div>
              </div>
            )}

            {/* Queue Position */}
            {queuePosition !== null && (
              <div className="text-center p-6 bg-muted/50 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-2">Your position in queue</p>
                <p className="text-4xl font-bold text-primary">#{queuePosition}</p>
              </div>
            )}
            
            {/* Pre-Alpha Info */}
            <div className="space-y-4 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-3">
                <Users className="h-6 w-6 text-primary mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <h3 className="font-semibold text-foreground text-lg">We're in Pre-Alpha</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    We're currently in the early stages of building something special. To ensure the best experience and gather valuable feedback, we're approving signups in small batches.
                  </p>
                </div>
              </div>
            </div>

            {/* What's Next */}
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                What Happens Next?
              </h3>
              <div className="space-y-3 pl-7">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    We review applications regularly and approve new users in batches
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    You'll receive an email at <span className="font-medium text-foreground">{user?.email}</span> as soon as you're approved
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Verified emails get priority access, so make sure to verify your email!
                  </p>
                </div>
              </div>
            </div>

            {/* Email Notification */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-medium text-foreground mb-1">We'll Keep You Updated</h3>
                  <p className="text-sm text-muted-foreground">
                    You'll receive notifications about your waitlist status and when you're ready to get started. We look forward to welcoming you soon!
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                Your spot is reserved. We appreciate your patience as we build something great together.
              </p>
              <Button onClick={signOut} variant="outline" className="w-full">
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Waitlist;
